/**
 * AppHeader Sync Middleware
 *
 * Redux middleware that intercepts appHeader actions and syncs state changes
 * to the Angular AppHeaderViewModel.
 *
 * The middleware checks the synchronizer's origin flag to prevent infinite loops:
 * - If origin is 'angular', the change came from Angular and we skip syncing back
 * - If origin is null, the change came from Redux and we sync to Angular
 */

import { type Middleware, type UnknownAction } from '@reduxjs/toolkit';
import { appHeaderActions } from '../redux/appHeaderSlice';
import { type TitleOpts, type ViewedStream } from '../types';
import { appHeaderSynchronizer, type SyncedProperty } from './appHeaderSynchronizer';

// Map action types to their corresponding sync property
const actionToPropertyMap: Record<string, SyncedProperty> = {
    [appHeaderActions.setHidden.type]: 'hidden',
    [appHeaderActions.toggleVisibility.type]: 'hidden',
    [appHeaderActions.setHasTopMessage.type]: 'hasTopMessage',
    [appHeaderActions.setShowCaret.type]: 'showCaret',
    [appHeaderActions.setShowAlternateHomeButton.type]: 'showAlternateHomeButton',
    [appHeaderActions.setTitle.type]: 'title',
    [appHeaderActions.setTitleOpts.type]: 'titleOpts',
    [appHeaderActions.setTitleHTML.type]: 'title',
    [appHeaderActions.setViewedStream.type]: 'viewedStream',
    [appHeaderActions.setMobileKeyboardShowing.type]: 'mobileKeyboardShowing',
};

// Type guard to check if an action is an appHeader action with payload
function isAppHeaderAction(action: unknown): action is UnknownAction & { payload: unknown } {
    return (
        typeof action === 'object' &&
        action !== null &&
        'type' in action &&
        typeof (action as UnknownAction).type === 'string'
    );
}

/**
 * Sync a specific action's payload to the Angular view model
 */
function syncActionToAngular(actionType: string, payload: unknown): void {
    switch (actionType) {
        case appHeaderActions.setHidden.type:
            appHeaderSynchronizer.syncFromRedux('hidden', payload as boolean);
            break;
        case appHeaderActions.toggleVisibility.type:
            appHeaderSynchronizer.syncFromRedux('hidden', !(payload as boolean));
            break;
        case appHeaderActions.setHasTopMessage.type:
            appHeaderSynchronizer.syncFromRedux('hasTopMessage', payload as boolean);
            break;
        case appHeaderActions.setShowCaret.type:
            appHeaderSynchronizer.syncFromRedux('showCaret', payload as boolean);
            break;
        case appHeaderActions.setShowAlternateHomeButton.type:
            appHeaderSynchronizer.syncFromRedux('showAlternateHomeButton', payload as boolean);
            break;
        case appHeaderActions.setTitle.type:
            appHeaderSynchronizer.syncFromRedux('title', payload as string | undefined);
            break;
        case appHeaderActions.setTitleOpts.type:
            appHeaderSynchronizer.syncFromRedux('titleOpts', payload as TitleOpts);
            break;
        case appHeaderActions.setTitleHTML.type: {
            const titlePayload = payload as { title?: string; opts?: TitleOpts };
            appHeaderSynchronizer.syncFromRedux('title', titlePayload.title);
            appHeaderSynchronizer.syncFromRedux('titleOpts', titlePayload.opts ?? {});
            break;
        }
        case appHeaderActions.setViewedStream.type:
            appHeaderSynchronizer.syncFromRedux('viewedStream', payload as ViewedStream | null);
            break;
        case appHeaderActions.setMobileKeyboardShowing.type:
            appHeaderSynchronizer.syncFromRedux('mobileKeyboardShowing', payload as boolean);
            break;
        case appHeaderActions.resetState.type:
            syncResetState();
            break;
        default:
            // Unknown action type - no sync needed
            break;
    }
}

/**
 * Sync all properties to their initial values for resetState action
 */
function syncResetState(): void {
    appHeaderSynchronizer.syncFromRedux('hidden', false);
    appHeaderSynchronizer.syncFromRedux('hasTopMessage', false);
    appHeaderSynchronizer.syncFromRedux('showCaret', false);
    appHeaderSynchronizer.syncFromRedux('showAlternateHomeButton', false);
    appHeaderSynchronizer.syncFromRedux('title', undefined);
    appHeaderSynchronizer.syncFromRedux('titleOpts', {});
    appHeaderSynchronizer.syncFromRedux('viewedStream', null);
    appHeaderSynchronizer.syncFromRedux('mobileKeyboardShowing', false);
}

/**
 * Middleware that syncs Redux appHeader state changes to Angular AppHeaderViewModel
 */
export const appHeaderSyncMiddleware: Middleware = _store => next => (action: unknown) => {
    const result = next(action);

    if (!isAppHeaderAction(action)) return result;

    // Check if this is an appHeader action we care about
    const isAppHeaderActionType =
        actionToPropertyMap[action.type] !== undefined || action.type === appHeaderActions.resetState.type;

    if (!isAppHeaderActionType) return result;

    // Skip if the change originated from Angular (to prevent infinite loops)
    if (appHeaderSynchronizer.getOrigin() === 'angular') return result;

    syncActionToAngular(action.type, action.payload);

    return result;
};
